import { AxiosError } from 'axios';
import { apiService } from '@/shared/services/apiServices';
import { VIEW_SERVICE_REQUEST_ERROR_MESSAGES } from '../errors/errorMessages';
import type { ViewServiceRequestDetail } from '../types/viewServiceRequestType';
import serviceRequestEndpoints from './serviceRequestEndpoints';

export interface ViewServiceRequestResult {
  success: boolean;
  data?: ViewServiceRequestDetail;
  error?: string;
  notFound?: boolean;
  forbidden?: boolean;
}

/**
 * Which detail endpoint to read.
 * - `'default'` → `GET /service-requests/{id}` (any request the caller may view).
 * - `'my-tasks'` → `GET /service-requests/my-tasks/{id}` (RR-914): self-scoped,
 *   returns the task only when assigned to the calling admin (otherwise 404).
 */
export type ViewServiceRequestSource = 'default' | 'my-tasks';

const isInvalidUuidError = (errorData: unknown): boolean => {
  if (errorData && typeof errorData === 'object') {
    const message = (errorData as { message?: unknown }).message;
    return typeof message === 'string' && message === 'Invalid UUID format';
  }
  return false;
};

const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 401 || status === 403) {
    return VIEW_SERVICE_REQUEST_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 500) {
    return VIEW_SERVICE_REQUEST_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (errorData && typeof errorData === 'object') {
    const obj = errorData as { message?: string; error?: string };
    if (obj.message) {
      return obj.message;
    }
    if (obj.error) {
      return obj.error;
    }
  }
  if (typeof errorData === 'string' && errorData.trim()) {
    return errorData;
  }
  return VIEW_SERVICE_REQUEST_ERROR_MESSAGES.DEFAULT_ERROR;
};

export const viewServiceRequest = async (
  serviceRequestId: string,
  source: ViewServiceRequestSource = 'default'
): Promise<ViewServiceRequestResult> => {
  const trimmedId = serviceRequestId?.trim();
  if (!trimmedId) {
    return {
      success: false,
      error: VIEW_SERVICE_REQUEST_ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  try {
    const endpoint =
      source === 'my-tasks'
        ? serviceRequestEndpoints.VIEW_MY_ASSIGNED_TASK(trimmedId)
        : serviceRequestEndpoints.VIEW_SERVICE_REQUEST(trimmedId);
    const result = await apiService.getAPI(endpoint, {
      skipGlobalErrorRedirect: true,
    });

    if (!result.error) {
      const payload = (result.data ?? {}) as {
        service_request?: ViewServiceRequestDetail;
        data?: ViewServiceRequestDetail;
      } & Partial<ViewServiceRequestDetail>;
      const detail = (payload.service_request ??
        payload.data ??
        payload) as ViewServiceRequestDetail;
      return {
        success: true,
        data: detail,
      };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: VIEW_SERVICE_REQUEST_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    if (result.status === 403) {
      return { success: false, forbidden: true };
    }

    if (result.status === 404 || isInvalidUuidError(result.data)) {
      return { success: false, notFound: true };
    }

    return {
      success: false,
      error: extractErrorMessage(result.data, result.status),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      if (isNetworkError) {
        return {
          success: false,
          error: VIEW_SERVICE_REQUEST_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      if (status === 404 || isInvalidUuidError(errorData)) {
        return { success: false, notFound: true };
      }

      return {
        success: false,
        error: extractErrorMessage(errorData, status),
      };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : VIEW_SERVICE_REQUEST_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
